import { Component, Input, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { DealerViewModel } from "src/domain/client";
import ISearchQueryParams from "src/domain/extentions/search-query-params";
import { DealerSelectionMethod } from "src/domain/trackingEnums";
import { SpaceValidator } from "src/domain/validators/noSpaceValidator";
import { FilterDealerPipe } from "src/pipes/filter-dealer.pipe";
import { ChannelRequestService } from "src/services/channel-request.service";
import { CookieService } from "src/services/cookie.service";
import { ErrorReporterService } from "src/services/error-reporter.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import { ThemeService } from "src/services/theme.service";

@Component({
  selector: "vw-car-and-dealer",
  templateUrl: "./car-and-dealer.component.html",
})
export class CarAndDealerComponent implements OnInit {
  @Input() caption?: string;
  @Input() ingress?: string;
  @Input() hero?: string;

  filterByModel = false;
  updateVehicleSaving = false;
  updateDealerSaving = false;

  regNrForm = new FormGroup({
    regNr: new FormControl("", [
      Validators.required,
      SpaceValidator.cannotContainSpace,
    ]),
  });

  dealerForm = new FormGroup({
    dealer: new FormControl("", [Validators.required]),
    selectedDealerId: new FormControl(NaN, [Validators.required]),
  });

  searchForm = new FormGroup({
    query: new FormControl("", [Validators.required]),
  });

  constructor(
    public modelAndDealerService: ModelAndDealerService,
    public cookieService: CookieService,
    public channelRequestService: ChannelRequestService,
    private errorReporterService: ErrorReporterService,
    private filterDealerPipe: FilterDealerPipe,
    private router: Router,
    private themeService: ThemeService,
  ) {
    // needed for the callback pipe
    this.filterDealer = this.filterDealer.bind(this);
  }

  ngOnInit(): void {
    this.initFormSubscribers();
  }

  toggleFilterByModel(): void {
    if (this.filterByModel) {
      this.modelAndDealerService.modelForm.reset();
    }

    this.filterByModel = !this.filterByModel;
  }

  clearRegNr(): void {
    this.regNrForm.patchValue({ regNr: "" });
  }

  updateVehicle(): void {
    if (!this.regNrForm.controls.regNr.valid) {
      return;
    }

    this.updateVehicleSaving = true;

    this.channelRequestService
      .updateChannelRequest(this.regNrForm.value.regNr)
      .then(() => {})
      .catch(this.errorReporterService.handle)
      .finally(() => {
        this.updateVehicleSaving = false;
      });
  }

  modelSelected(): void {
    if (!this.modelAndDealerService.modelForm.valid) {
      return;
    }

    const model = this.modelAndDealerService.models.find(
      (_) => _.name === this.modelAndDealerService.modelForm.value.model,
    );

    this.updateVehicleSaving = true;

    this.channelRequestService
      .updateChannelRequest(undefined, undefined, undefined, model)
      .then(() => {
        this.toggleFilterByModel();
      })
      .catch(this.errorReporterService.handle)
      .finally(() => {
        this.updateVehicleSaving = false;
      });
  }

  updateDealer(): void {
    const dealer = this.modelAndDealerService.dealers.find(
      ({ dealerId }) =>
        String(dealerId) === String(this.dealerForm.value.selectedDealerId),
    );

    this.updateDealerSaving = true;
    this.channelRequestService
      .updateChannelRequest(undefined, dealer, DealerSelectionMethod.SEARCH)
      .then(() => {})
      .catch(this.errorReporterService.handle)
      .finally(() => {
        this.updateDealerSaving = false;
      });
  }

  openSelectDealerModal(): void {
    this.modelAndDealerService.openSelectDealer();
  }

  selectDealer(dealer: DealerViewModel): void {
    this.dealerForm.patchValue(
      {
        selectedDealerId: dealer.dealerId,
        dealer: dealer.name,
      },
      { emitEvent: false },
    );
  }

  filterDealer(dealer: DealerViewModel): boolean {
    const formDealer = this.dealerForm.value.dealer ?? "";
    return this.filterDealerPipe.transform(formDealer, dealer);
  }

  searchSubmit(): void {
    if (!this.searchForm.valid) {
      return;
    }

    this.router.navigate([this.themeService.brand, "search"], {
      queryParams: {
        query: this.searchForm.value.query ?? null,
      } satisfies ISearchQueryParams,
    });
  }

  //////////

  private initFormSubscribers(): void {
    // reset selected dealer when the text box in the typeahead changes

    this.dealerForm.get("dealer")?.valueChanges.subscribe(() => {
      this.dealerForm.patchValue({
        selectedDealerId: undefined,
      });
    });
  }
}
